import React from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import { useDraggable } from "@dnd-kit/core";

// Generic Kanban primitives used by Campaigns and CRM pages.
export const KanbanColumn = ({ id, title, count, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`w-72 rounded-xl border bg-white/[0.02] p-3 transition-colors ${
        isOver ? "border-blue-500/50 bg-blue-500/[0.05]" : "border-white/8"
      }`}
      data-testid={`kanban-column-${id}`}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <h3 className="text-xs uppercase tracking-wider text-zinc-400">{title}</h3>
        <span className="text-[10px] text-zinc-500">{count}</span>
      </div>
      <div className="space-y-2 min-h-[40px]">{children}</div>
    </div>
  );
};

export const KanbanCard = ({ id, children, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 50,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Only fire onClick if not dragging
        if (!isDragging && onClick) onClick(e);
      }}
      className={`rounded-lg border border-white/8 bg-zinc-950 p-3 hover:border-blue-500/30 transition-colors cursor-grab active:cursor-grabbing ${
        isDragging ? "opacity-50" : ""
      }`}
      data-testid={`kanban-card-${id}`}
    >
      {children}
    </div>
  );
};

export const KanbanBoard = ({ columns, itemsByColumn, renderCard, onMove, dragOverlay }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const overId = String(over.id);
    // Over could be a column id (droppable) or a card id (we treat card hovers as column = card's column)
    const targetColumn = columns.find((c) => c.id === overId)?.id;
    if (!targetColumn) return;
    const activeId = String(active.id);
    // Find the current column of active
    const currentColumn = Object.keys(itemsByColumn).find((col) =>
      (itemsByColumn[col] || []).some((it) => it.id === activeId)
    );
    if (currentColumn && currentColumn !== targetColumn) {
      onMove(activeId, targetColumn);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
      <div className="overflow-x-auto pb-2">
        <div className="flex gap-4 min-w-max">
          {columns.map((col) => (
            <KanbanColumn key={col.id} id={col.id} title={col.title} count={(itemsByColumn[col.id] || []).length}>
              {(itemsByColumn[col.id] || []).length === 0 && (
                <div className="text-xs text-zinc-600 italic px-1 py-2">Drop here</div>
              )}
              {(itemsByColumn[col.id] || []).map((item) => renderCard(item))}
            </KanbanColumn>
          ))}
        </div>
      </div>
      <DragOverlay>{dragOverlay}</DragOverlay>
    </DndContext>
  );
};
